import { z } from 'zod';
import { apiEnvelope } from '../envelope';
import {
  ESCALATION_COMMENT_MAX_LENGTH,
  escalationEventKindSchema,
  type EscalationEventKind,
} from './schema';

// ─── an escalation's history ────────────────────────────────────────────────
//
// Wire vocabulary for reading one escalation's history (its event log, oldest
// first) and adding a comment to it. Both sit under the member path, beside
// the other escalation writes, and are served to the same staff who can open
// the review panel.
//
// The history read is the one response that returns stored staff text — a
// comment, a release or take-over reason, a contact attempt's note. No write
// response echoes any of it, and none of it is telemetry.

/** How adding a comment ended. Both are a 200, as on contact attempts. */
export const ESCALATION_COMMENT_OUTCOME = ['commented', 'already_handled'] as const;
export const escalationCommentOutcomeSchema = z.enum(ESCALATION_COMMENT_OUTCOME);
export type EscalationCommentOutcome = z.infer<typeof escalationCommentOutcomeSchema>;

/**
 * Body of the staff POST that adds a comment.
 *
 * `.strict()`: a request the server validates, so a body naming an author is
 * refused rather than ignored — the author comes from the authorized-staff
 * gate (ADR-135, ADR-087). Trimmed, and refused when blank: a comment is its
 * text, so there is no "empty comment" to store.
 */
export const escalationCommentRequestSchema = z
  .object({
    text: z.string().trim().min(1).max(ESCALATION_COMMENT_MAX_LENGTH),
  })
  .strict();
export type EscalationCommentRequest = z.input<typeof escalationCommentRequestSchema>;

/** Not strict: Atlas parses responses with `schema.parse()` (ADR-135 Rule 1). */
export const escalationCommentDataSchema = z.object({
  outcome: escalationCommentOutcomeSchema,
});
export type EscalationCommentData = z.infer<typeof escalationCommentDataSchema>;
export const escalationCommentResponseSchema = apiEnvelope(escalationCommentDataSchema);
export type EscalationCommentResponse = z.infer<typeof escalationCommentResponseSchema>;

/**
 * One entry of an escalation's history.
 *
 * `kind` is a string on the wire, not the event-kind enum. Kinds are appended
 * over time, and a response narrowed to today's enum would fail every Atlas
 * bundle older than the deploy that adds one (ADR-135 Rule 3). A client reads
 * it through `knownEscalationEventKind` and renders a kind it does not know
 * generically.
 *
 * Names, never ids: `actorName` is null only for a `raised` entry, which no
 * person performed; `previousHolderName` is set where the event displaced a
 * holder. `text` is the stored note — a comment, a reason, or a contact
 * attempt's note. `backfilled` marks an entry reconstructed from data that
 * predates its kind.
 *
 * Not `.strict()`, for the reason above.
 */
export const escalationHistoryEntrySchema = z.object({
  id: z.string().uuid(),
  kind: z.string(),
  occurredAt: z.iso.datetime(),
  actorName: z.string().nullable(),
  previousHolderName: z.string().nullable(),
  // Whom an assignment made the holder: set on an `assigned` entry, null on
  // every other kind. Optional because an API deployed before the field sends
  // none, which reads as no assignee (ADR-135 Rule 6).
  assigneeName: z.string().nullable().optional(),
  text: z.string().nullable(),
  backfilled: z.boolean(),
});
export type EscalationHistoryEntry = z.infer<typeof escalationHistoryEntrySchema>;

/** The most entries one read returns: the most recent, oldest first. */
export const ESCALATION_HISTORY_MAX_ENTRIES = 500;

/**
 * An escalation's history, oldest first. `truncated` says older entries exist
 * beyond `ESCALATION_HISTORY_MAX_ENTRIES`, so a short list never reads as the
 * whole story when it is not.
 */
export const escalationHistoryDataSchema = z.object({
  entries: z.array(escalationHistoryEntrySchema),
  truncated: z.boolean(),
});
export type EscalationHistoryData = z.infer<typeof escalationHistoryDataSchema>;
export const escalationHistoryResponseSchema = apiEnvelope(escalationHistoryDataSchema);
export type EscalationHistoryResponse = z.infer<typeof escalationHistoryResponseSchema>;

/**
 * The event kind an entry carries, or `unknown` for one this build does not
 * know — a kind added by a later release. The caller renders `unknown`
 * generically instead of failing the history.
 */
export function knownEscalationEventKind(kind: string): EscalationEventKind | 'unknown' {
  const parsed = escalationEventKindSchema.safeParse(kind);
  return parsed.success ? parsed.data : 'unknown';
}
